"use client";
import { useTranslations } from "next-intl";
import * as dc from "@/components/layout/list/defaultType";
import { Container, DataList, Flex, Skeleton } from "@radix-ui/themes";
import type { DataElement, DataInputParams, SeriesScaleRecord, SeriesScaleType, TimeLineProps, TimeLineXY } from "./inputType";
import { Form, Skeleton as AntSkeleton, Radio, Select, FormInstance, Button, Switch } from "antd";
import { Debug } from "@/util/debug";
import { GetDataInputElement, getDayjsValueStrCommon, getParamFields, runFormFunction } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";

import { ScaleTime } from "d3-scale";
import {
  SliceTooltipProps,
  PointTooltipProps,
  LineSeries,
  PointTooltipComponent,
  ResponsiveLine,
  ComputedSeries,
  LineGenerator,
  AllowedValue,
} from "@nivo/line";
import { LineDatum, TimeLineSeries } from "../list/defaultType";
import DataInput, { GetDataLabel } from "../data/dataInput";
import { CSSProperties, Dispatch, RefObject, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CartesianMarkerProps, DatumValue } from "@nivo/core";
import { cn } from "@/lib/utils";
import { defaultTimelineData, nivoTheme, tempTimelineData, timelineDefs, timelineFill } from "../list/defaultType";
import dayjs from "dayjs";
import { checkMobile } from "@/util/checkMobile";
import { checkEmpty, getJsonFromStr } from "@/util/stringutil";
import Icon from "@/components/common/icon/icon";
import { checkSafari } from "@/util/apputil";
import { exportChartAsPNG } from "@/util/fileutil";
import { AlertContext } from "@/components/context/alertContext";
import { time } from "console";
type CustomLineLayerProps = {
  series: readonly ComputedSeries<LineSeries>[];
  lineGenerator: LineGenerator;
  colorMap: Record<string, { color: string; width?: number | string; dashArray?: number | string }> | undefined;
};
const CustomLines = ({ series, lineGenerator, colorMap }: CustomLineLayerProps) => (
  <g>
    {series.map((s: any) => (
      <path
        key={s.id}
        d={lineGenerator(s.data.map((d: any) => d.position)) ?? undefined}
        fill="none"
        stroke={(colorMap ? colorMap[s.id].color : undefined) ?? "#aeaeae"}
        strokeWidth={(colorMap ? colorMap[s.id].width : undefined) ?? 2}
        strokeDasharray={(colorMap ? colorMap[s.id].dashArray : undefined) ?? "none"}
      />
    ))}
  </g>
);
const timeformat = "mm:ss";
const utcOffset = dayjs().utcOffset() * 60000;
export const getTimelineShowTimeObject = (series: string[], key: string) => {
  const result: Record<string, string> = {};
  series.forEach((serie) => {
    result[serie] = key + "_" + serie;
  });
  return result;
};
export const timelineChartChange = (form: FormInstance<any> | undefined, e: any) => {
  if (form) {
    form.setFieldsValue({ timeline_chart: true });
  }
};

type LineLayerProps = {
  xScale: (value: Date | number | string) => number;
  innerHeight: number;
  innerWidth: number;
};
type MovingVerticalBarProps = LineLayerProps & {
  startTerm: number;
  xMax: Date | undefined;
  fillColor: string | undefined;
};
const MovingVerticalBar = ({ xScale, innerHeight, startTerm, fillColor, xMax }: MovingVerticalBarProps) => {
  const max = xMax ? dayjs(xMax).valueOf() : 0;
  if (startTerm > 0) {
    const x = xScale(startTerm > max ? max : startTerm);
    if (typeof x !== "number") return null;
    return <line x1={x / 2} x2={x / 2} y1={0} y2={innerHeight} stroke={fillColor ?? "var(--accent-a3)"} strokeWidth={x} />;
  }
};
type SerieWrite = {
  [x: string]: any;
  id: string | number;
  compare?: string;
  color?: string;
  data: LineDatum[];
};
type TimeLineData =
  | {
      data: TimeLineSeries[];
      min: TimeLineXY;
      max: TimeLineXY;
      valueMax: TimeLineXY;
      checkPoint?: Record<string, Date>;
      comparePoint?: Record<string, Record<string, Date>>;
      checkValue?: number[];
      seriesScale?: SeriesScaleRecord;
      tempDataTitle: Record<string, string>;
      notChange: boolean;
    }
  | undefined;

const getSerieTemp = (
  t: TransitionType,
  transitionId: boolean,
  key: string,
  serie: string,
  append: string,
  values: any,
  // form: FormInstance<any> | undefined,
  // calcValues: string[] | RegExp | undefined,
  baseTime: string | undefined,
  inputMode: boolean,
  showtimes: Record<string, Record<string, string>>,
  log_data: any,
  serieMap: Record<string, string>,
  xPushed: Record<number, string[]>,
  timeLimit: number,
  min: TimeLineXY,
  max: TimeLineXY,
  x: number | undefined,
  xScale: 1 | -1,
  yScale: number,
  serieScale: SeriesScaleType | undefined,
  useBefore: boolean,
) => {
  const yKey = showtimes[key][serie];
  const y =
    // inputMode &&
    // form &&
    // calcValues &&
    // ((calcValues instanceof Array && calcValues.includes(yKey)) || (calcValues instanceof RegExp && calcValues.test(yKey)))
    //   ? form.getFieldValue(yKey)
    //   :
    values[yKey] ?? (log_data && log_data[x + ""] ? log_data[x + ""][serie] : undefined);

  if (!serieMap[serie + append]) {
    serieMap[serie + append] = transitionId ? t(serie + append) : serie + append;
  }

  const serieStr = serieMap[serie + append];
  // if (x > timeLimit) {
  //   Debug("TimeLimit Over", key, serie, x, y, dayjs(x));
  // }
  if (x !== undefined && y !== undefined && y !== null && x < timeLimit) {
    const temp = {
      x: dayjs(x).toDate(),
      y: y * yScale * (serieScale?.calcScale ?? 1),
      seriesId: serie + append,
    };
    if (!xPushed[x]) {
      xPushed[x] = [];
    }
    xPushed[x].push(serie);
    if (min.x === undefined || min.x > temp.x) {
      min.x = temp.x;
    }
    if (min.y === undefined || min.y > temp.y) {
      if (useBefore || xScale >= 0) {
        min.y = temp.y;
      }
    }
    if (max.x === undefined || max.x < temp.x) {
      max.x = temp.x;
    }
    if (useBefore || xScale >= 0) {
      if (max.y === undefined || max.y < temp.y) {
        max.y = temp.y;
      }
      if (serieScale && (serieScale.max === undefined || serieScale.max < temp.y)) {
        serieScale.max = temp.y;
      }
    }
    return { serieStr, temp };
  } else {
    return { serieStr, temp: undefined };
  }
};
const checkLimit = (params: TimeLineProps, dataY: number | AllowedValue, id?: string) => {
  const limit = params.pointLabelLimit ?? undefined;
  const idlimit = id && params.pointLabelIdLimit ? params.pointLabelIdLimit[id] : undefined;
  return (
    (!limit ||
      (limit &&
        (!limit.min || (typeof dataY === "number" && limit.min && limit.min <= dataY)) &&
        (!limit.max || (typeof dataY === "number" && limit.max && limit.max >= dataY)))) &&
    (!idlimit ||
      (idlimit &&
        (!idlimit.min || (typeof dataY === "number" && idlimit.min && idlimit.min <= dataY)) &&
        (!idlimit.max || (typeof dataY === "number" && idlimit.max && idlimit.max >= dataY))))
  );
};

const dataConverter = (t: TransitionType, org: TimeLineData, params: DataInputParams, init?: boolean): TimeLineData => {
  const baseTime = params.timelineProp?.baseTime ?? params.indexKey?.toString();
  const dataMap: Record<string, SerieWrite> = {};
  // const min: TimeLineXY = { x: params.timelineProp?.min?.x, y: params.timelineProp?.min?.y };
  // const max: TimeLineXY = { x: params.timelineProp?.max?.x, y: params.timelineProp?.max?.y };
  const min: TimeLineXY = { x: params.timelineProp?.min?.x, y: undefined };
  const max: TimeLineXY = { x: undefined, y: undefined };
  const compareBaseTimeValue =
    params.form && params.timelineProp?.compareBaseTime
      ? params.form.getFieldsValue(
          params.timelineProp?.compareBaseTimeInput
            ? [params.timelineProp.compareBaseTime, params.timelineProp.compareBaseTimeInput]
            : [params.timelineProp.compareBaseTime],
        )
      : undefined;
  const compareBaseTime =
    compareBaseTimeValue &&
    params.timelineProp?.compareBaseTime &&
    compareBaseTimeValue[params.timelineProp.compareBaseTime] &&
    !checkEmpty(compareBaseTimeValue[params.timelineProp.compareBaseTime]) &&
    compareBaseTimeValue[params.timelineProp.compareBaseTime] !== baseTime
      ? compareBaseTimeValue[params.timelineProp.compareBaseTime]
      : undefined;
  const cbt = compareBaseTime
    ? {
        point: compareBaseTime !== dc.input_direct ? compareBaseTime : undefined,
        pointTime: 0,
        timeShift:
          compareBaseTimeValue &&
          compareBaseTime &&
          params.timelineProp?.compareBaseTimeInput &&
          compareBaseTime === dc.input_direct &&
          !checkEmpty(compareBaseTimeValue[params.timelineProp.compareBaseTimeInput])
            ? (compareBaseTimeValue[params.timelineProp.compareBaseTimeInput] as number) * 1000
            : undefined,
        compare: {} as Record<string, number>,
        pointApply: [] as string[],
      }
    : undefined;
  Debug("compareBaseTime : ", compareBaseTimeValue, cbt);

  const checkPoint: Record<string, Date> = {};
  const comparePoint: Record<string, Record<string, Date>> = {};
  const checkValue: number[] = [];
  const operationProp = params.timelineProp?.dataOperation ?? undefined;
  const operationKeys = operationProp ? Object.keys(operationProp) : undefined;
  const rorProp = params.timelineProp?.ror ?? undefined;
  const rorKeys = rorProp ? Object.keys(rorProp) : undefined;
  const seriesScale: SeriesScaleRecord | undefined = params.timelineProp?.ySeriesScale
    ? params.timelineProp?.ySeriesScale instanceof Array
      ? params.timelineProp.ySeriesScale.reduce<Record<string, SeriesScaleType>>((acc, key) => {
          acc[key] = { toFixed: 0, subScale: 1 };
          return acc;
        }, {})
      : { ...params.timelineProp.ySeriesScale }
    : rorKeys
      ? {}
      : undefined;

  if (seriesScale) {
    Object.keys(seriesScale).forEach((key) => {
      seriesScale[key].calcScale = (seriesScale[key].subScale ?? 1) * (seriesScale[key].scale ?? 1);
    });
  }
  let notChange = true;
  if (params.timelineProp?.checkValues) {
    params.timelineProp?.checkValues.forEach((item) => {
      if (typeof item === "number") checkValue.push(item);
    });
  }
  // Debug(params.timelineProp?.checkValues, checkValue);
  const checkIds = params.timelineProp?.checkIds
    ? typeof params.timelineProp.checkIds == "string"
      ? [params.timelineProp?.checkIds]
      : params.timelineProp.checkIds
    : undefined;
  const useBefore: boolean =
    params.timelineProp?.beforeId !== undefined
      ? (params.form?.getFieldValue(params.timelineProp.beforeId) ?? params.values[params.timelineProp.beforeId] ?? true)
      : true;
  const compare = "_compare";
  const tempDataTitle: Record<string, string> = {};

  if (!init && params.values && params.timelineProp && params.timelineProp.showTimes) {
    const timeLimit = params.timelineProp.timeLimit ?? (params.timelineProp.hours ? 86400000 : 3600000);
    const showtimes = params.timelineProp.showTimes;
    const inputMode = params.inputMode === "view";
    const timeLimitConv = inputMode ? timeLimit : dayjs(timeLimit + utcOffset).valueOf();
    const yScale = 1 / (inputMode && params.timelineProp?.yViewScale ? params.timelineProp.yViewScale : 1);
    const values: Record<string, any> = inputMode ? { ...params.values } : { ...(params.form?.getFieldsValue() ?? {}) };
    const calcValues = params.timelineProp.calcValues;
    const fieldsValues = inputMode
      ? calcValues instanceof Array
        ? params.form?.getFieldsValue(calcValues)
        : params.form?.getFieldsValue()
      : {};

    if (inputMode && params.form && calcValues) {
      Object.keys(fieldsValues).forEach((key) => {
        if (calcValues instanceof Array || (calcValues instanceof RegExp && calcValues.test(key))) {
          const tempValue = fieldsValues[key];
          // Debug("dataConverter : " + key, tempValue);
          if (!checkEmpty(tempValue)) {
            if (typeof tempValue === "number") {
              values[key] = tempValue / yScale;
            } else if (tempValue.$isDayjsObject) {
              values[key] = dayjs(tempValue).valueOf() + utcOffset;
            } else {
              values[key] = tempValue;
            }
          }
        }
      });
    }

    // Debug("dataConverter showtimes : " + params.name, params.form, showtimes, calcValues, values, fieldsValues);

    const temp_data_check: Record<string, any> =
      params.name && params.formMemo && params.formMemo[params.name] && params.formMemo[params.name].tempData
        ? params.formMemo[params.name].tempData
        : undefined;
    // Debug("dataConverter Temp Data : " + params.name, temp_data);
    const temp_data_list: Record<string, any> = {};
    if (temp_data_check) {
      Object.keys(temp_data_check).forEach((id) => {
        if (temp_data_check[id].compare) {
          temp_data_list[id] = { ...temp_data_check[id] };
          Debug("dataConverter temp data : " + id, temp_data_list[id]);
        }
      });
    }

    if (baseTime) {
      if (values) {
        if (values[baseTime] !== undefined) {
          values[baseTime] = 0;
        } else {
          values[baseTime] = 0;
        }
      }
      if (temp_data_list) {
        Object.keys(temp_data_list).forEach((id) => {
          const temp_data = temp_data_list[id];
          if (temp_data[baseTime] !== undefined) {
            temp_data[baseTime] = 0;
          } else {
            temp_data[baseTime] = 0;
          }
        });
      }
    }

    const log_data: Record<string, Record<string, number>> = params.name &&
    params.formMemo &&
    params.formMemo[params.name] &&
    params.formMemo[params.name].log
      ? params.formMemo[params.name].log
      : params.timelineProp.logId
        ? params.form?.getFieldInstance(params.timelineProp.logId)
          ? getJsonFromStr(params.form.getFieldValue(params.timelineProp.logId))
          : getJsonFromStr(params.values[params.timelineProp.logId])
        : undefined;

    const xPushed: Record<number, string[]> = {};
    const transitionId = params.timelineProp?.transitionId ?? false;
    const keys = Object.keys(showtimes);
    const serieMap: Record<string, string> = {};
    keys.map((key) => {
      const series = Object.keys(showtimes[key]);
      const xScale = params.timelineProp?.beforeTime?.includes(key) ? -1 : 1;
      if (!useBefore && xScale < 0) {
        //return;
      }
      const keyValue = values[key];
      const x =
        keyValue !== undefined && keyValue !== null
          ? baseTime === key
            ? 0
            : inputMode
              ? keyValue * xScale
              : dayjs(keyValue + utcOffset).valueOf() * xScale
          : undefined;
      if (x !== undefined && cbt && cbt.point === key) {
        cbt.pointTime = x;
      }

      series.map((serie) => {
        const serieScale = seriesScale ? seriesScale[serie] : undefined;
        const serieObj = getSerieTemp(
          t,
          transitionId,
          key,
          serie,
          "",
          values,
          baseTime,
          inputMode,
          showtimes,
          log_data,
          serieMap,
          xPushed,
          timeLimitConv,
          min,
          max,
          x,
          xScale,
          yScale,
          serieScale,
          useBefore,
        );
        // Debug("serieObj : ", key, serie, x, serieObj);
        if (serieObj.temp) {
          if (operationKeys && operationProp) {
            operationKeys.forEach((operationKey) => {
              const operetion = operationProp[operationKey];
              if (!operetion.startTerm && (operetion.id ?? operationKey) === serieObj.serieStr && operetion.startId === key) {
                operetion.startDate = serieObj.temp.x;
              } else if (!operetion.endTerm && (operetion.id ?? operetion) === serieObj.serieStr && operetion.endId === key) {
                operetion.endDate = serieObj.temp.x;
              }
            });
          }
          if (rorKeys && rorProp) {
            rorKeys.forEach((rorkey) => {
              const ror = rorProp[rorkey];
              if (!ror.startTerm && (ror.rorSeries ?? rorkey) === serieObj.serieStr && ror.startId === key) {
                ror.startDate = serieObj.temp.x;
              } else if (!ror.endTerm && (ror.rorSeries ?? rorkey) === serieObj.serieStr && ror.endId === key) {
                ror.endDate = serieObj.temp.x;
              }
            });
          }

          if (!checkPoint[key]) {
            checkPoint[key] = serieObj.temp.x;
          }
          //상단 라인 추가
          if (checkIds && checkIds.includes(serie) && params.timelineProp?.checkValues && params.timelineProp.checkValues.includes(key)) {
            checkValue.push(serieObj.temp.y);
          }
          if (!dataMap[serieObj.serieStr]) {
            dataMap[serieObj.serieStr] = { id: serieObj.serieStr, data: [serieObj.temp] };
          } else {
            dataMap[serieObj.serieStr].data.push(serieObj.temp);
          }
        }

        if (temp_data_list) {
          Object.keys(temp_data_list).forEach((id, index) => {
            const temp_data = temp_data_list[id].data;
            const compare_str = compare + (index + 1);
            tempDataTitle[serie + compare_str] = temp_data_list[id].title;

            if (temp_data) {
              const tempKeyValue = temp_data[key];
              const tempx =
                tempKeyValue !== undefined && tempKeyValue !== null ? (baseTime === key ? 0 : tempKeyValue * xScale) : undefined;

              if (cbt && tempx !== undefined && cbt.point === key) {
                cbt.compare[compare_str] = cbt.pointTime - tempx;
              }

              const tempSerieObj = getSerieTemp(
                t,
                transitionId,
                key,
                serie,
                compare_str,
                temp_data,
                baseTime,
                true,
                showtimes,
                log_data,
                serieMap,
                xPushed,
                timeLimitConv,
                min,
                max,
                tempx,
                xScale,
                1 / (params.timelineProp?.yViewScale ?? 1),
                serieScale,
                useBefore,
              );
              // Debug("temp_data : ", key, serie, tempKeyValue, tempx, timeLimit, tempSerieObj);

              if (tempSerieObj.temp) {
                if (operationKeys && operationProp) {
                  operationKeys.forEach((operationKey) => {
                    const operetion = operationProp[operationKey];
                    if (!operetion.startTerm && (operetion.id ?? operationKey) === tempSerieObj.serieStr && operetion.startId === key) {
                      operetion.startDate = tempSerieObj.temp.x;
                    } else if (!operetion.endTerm && (operetion.id ?? operetion) === tempSerieObj.serieStr && operetion.endId === key) {
                      operetion.endDate = tempSerieObj.temp.x;
                    }
                  });
                }

                if (rorKeys && rorProp) {
                  rorKeys.forEach((rorkey) => {
                    const ror = rorProp[rorkey];
                    if (!ror.startTerm && (ror.rorSeries ?? rorkey) === tempSerieObj.serieStr && ror.startId === key) {
                      ror.startDate = tempSerieObj.temp.x;
                    } else if (!ror.endTerm && (ror.rorSeries ?? rorkey) === tempSerieObj.serieStr && ror.endId === key) {
                      ror.endDate = tempSerieObj.temp.x;
                    }
                  });
                }

                if (!comparePoint[compare_str]) {
                  comparePoint[compare_str] = {};
                }
                if (comparePoint[compare_str]) {
                  if (!comparePoint[compare_str][key]) {
                    comparePoint[compare_str][key] = tempSerieObj.temp.x;
                  }
                }
                if (!dataMap[tempSerieObj.serieStr]) {
                  dataMap[tempSerieObj.serieStr] = { id: tempSerieObj.serieStr, compare: compare_str, data: [tempSerieObj.temp] };
                } else {
                  dataMap[tempSerieObj.serieStr].data.push(tempSerieObj.temp);
                }
              }
            }
          });
        }
      });
    });

    if (log_data) {
      // Debug(log_data);
      Object.entries(log_data).map(([logStr, values]) => {
        const log = Number(logStr);
        if (!useBefore && log < 0) {
          //return;
        }
        const series = Object.keys(values);
        series.map((serie: string) => {
          if (values[serie] && (!xPushed[log] || !xPushed[log].includes(serie))) {
            const serieScale = seriesScale ? seriesScale[serie] : undefined;
            const serieStr = serieMap[serie];
            const temp = {
              x: dayjs(log).toDate(),
              y: values[serie] * (serieScale?.calcScale ?? 1),
            };

            if (min.x === undefined || min.x > temp.x) {
              min.x = temp.x;
            }
            if (min.y === undefined || min.y > temp.y) {
              if (useBefore || log >= 0) {
                min.y = temp.y;
              }
            }
            if (max.x === undefined || max.x < temp.x) {
              max.x = temp.x;
            }
            if (useBefore || log >= 0) {
              if (max.y === undefined || max.y < temp.y) {
                max.y = temp.y;
              }
              if (serieScale && (serieScale.max === undefined || serieScale.max < temp.y)) {
                serieScale.max = temp.y;
              }
            }

            if (!dataMap[serieStr]) {
              dataMap[serieStr] = { id: serieStr, data: [temp] };
            } else {
              dataMap[serieStr].data.push(temp);
            }
          }
        });
      });
    }
    if (temp_data_list) {
      Object.keys(temp_data_list).forEach((id, index) => {
        const temp_data = temp_data_list[id].data;
        const compare_str = compare + (index + 1);
        // Debug("temp_data :" + compare_str, temp_data);
        if (temp_data?.roasting_chart_log) {
          const temp_log: Record<string, Record<string, number>> = getJsonFromStr(temp_data.roasting_chart_log);
          // Debug("compare_str :" + compare_str, temp_log);
          Object.entries(temp_log).map(([logStr, values]) => {
            const log = Number(logStr);
            if (!useBefore && log < 0) {
              //return;
            }
            const series = Object.keys(values);
            series.map((serie: string) => {
              if (values[serie] && (!xPushed[log] || !xPushed[log].includes(serie))) {
                const serieScale = seriesScale ? seriesScale[serie] : undefined;
                // const serieScaleCalc = (serieScale?.scale ?? 1) * (serieScale?.subScale ?? 1);
                const serieStr = serieMap[serie + compare_str];
                const temp = {
                  x: dayjs(log).toDate(),
                  y: values[serie] * (serieScale?.calcScale ?? 1),
                };

                if (min.x === undefined || min.x > temp.x) {
                  min.x = temp.x;
                }
                if (min.y === undefined || min.y > temp.y) {
                  if (useBefore || log >= 0) {
                    min.y = temp.y;
                  }
                }
                if (max.x === undefined || max.x < temp.x) {
                  max.x = temp.x;
                }
                if (useBefore || log >= 0) {
                  if (max.y === undefined || max.y < temp.y) {
                    max.y = temp.y;
                  }
                  if (serieScale && (serieScale.max === undefined || serieScale.max < temp.y)) {
                    serieScale.max = temp.y;
                  }
                }

                if (!dataMap[serieStr]) {
                  dataMap[serieStr] = { id: serieStr, compare: compare_str, data: [temp] };
                } else {
                  dataMap[serieStr].data.push(temp);
                }
              }
            });
          });
        }
      });
    }
  }

  // Debug("입력", dataMap);
  // Debug("ROR", rorProp, cbt);

  const data: TimeLineSeries[] = Object.values(dataMap).map((tempData) => {
    return {
      ...tempData,
      data: [...tempData.data].sort((a, b) => {
        const ax = dayjs(a.x).valueOf();
        const bx = dayjs(b.x).valueOf();

        if (isNaN(ax) && isNaN(bx)) return 0;
        if (isNaN(ax)) return 1;
        if (isNaN(bx)) return -1;

        return ax - bx;
      }),
    };
  });

  if (data.length == 0 && checkIds) {
    data.push({ data: [{ x: min.x ?? null, y: min.y ?? null }], id: "" });
  }
  //실제 max값 체크
  const valueMax: TimeLineXY = { x: max.x, y: max.y };

  if (seriesScale && !checkEmpty(seriesScale)) {
    Object.keys(seriesScale).forEach((serie) => {
      const serieScale = seriesScale[serie];
      // if (serieScale.scale !== undefined && serieScale.toFixed && serieScale.toFixed !== 0) {
      //   data.forEach((value) => {
      //     if (value.id === serie) {
      //       value.data.forEach((tempData, index) => {
      //         if (value.data[index].y) {
      //           value.data[index].y = Number(value.data[index].y);
      //         }
      //       });
      //     }
      //   });
      // } else
      if (serieScale.scale === undefined && serieScale.max !== undefined && max.y !== undefined && serieScale.max !== 0) {
        serieScale.scale = Math.floor(max.y / serieScale.max);
        data.forEach((value) => {
          if (value.id === serie) {
            value.data.forEach((tempData) => {
              if (tempData.y && serieScale.scale !== undefined && serieScale.scale !== 1) {
                // const temp = tempData.y * serieScale.scale;
                // tempData.y = serieScale.toFixed && serieScale.toFixed !== 0 ? Number(temp.toFixed(serieScale.toFixed)) : temp;
                tempData.y = tempData.y * serieScale.scale;
              }
            });
          }
        });
      }
    });
  }

  //
  if (min.x === undefined && params.timelineProp?.min?.x !== undefined) {
    min.x = params.timelineProp?.min?.x;
  }
  if (min.y === undefined && params.timelineProp?.min?.y !== undefined) {
    min.y = params.timelineProp?.min?.y;
  }
  if (max.x === undefined && params.timelineProp?.max?.x !== undefined) {
    max.x = params.timelineProp?.max?.x;
  }
  if (max.y === undefined && params.timelineProp?.max?.y !== undefined) {
    max.y = params.timelineProp?.max?.y;
  }

  if (!useBefore && min.x !== undefined && min.x.valueOf() < 0) {
    min.x = new Date(0);
  }

  if (cbt) {
    data.forEach((value) => {
      if (value.compare && cbt) {
        const timeShift = cbt.timeShift ?? cbt.compare[value.compare];
        // Debug("timeShift", value, value.compare, timeShift);
        if (timeShift !== undefined) {
          if (!useBefore && min.x !== undefined && min.x.valueOf() > timeShift) {
            min.x = new Date(timeShift);
          }
          value.data.forEach((tempData) => {
            if (timeShift !== undefined && tempData.x instanceof Date) {
              tempData.x = dayjs(tempData.x).add(timeShift).toDate();
            }
          });
          if (!cbt.pointApply.includes(value.compare)) {
            cbt.pointApply.push(value.compare);
            const tempPoint = comparePoint[value.compare];
            const tempKeys = Object.keys(tempPoint);
            if (baseTime && value.compare) {
              // tempPoint[baseTime] = dayjs(timeShift).toDate();
            }
            tempKeys.forEach((key) => {
              if (value.compare) {
                const tempvalue = tempPoint[key].valueOf();
                // Debug(value.compare, key, tempvalue, tempPoint[key]);
                tempPoint[key] = dayjs(tempvalue).add(timeShift).toDate();
                // Debug(value.compare, key, tempvalue, comparePoint[value.compare][key]);
              }
            });
          }
        }
      }
    });
  }

  if (min.x && min.x.getTime() !== 0) {
    if (params.timelineProp?.xFloorConst) {
      min.x = new Date(min.x.getTime() - params.timelineProp.xFloorConst);
    }
    if (params.timelineProp?.xFloorScale) {
      min.x = new Date(Math.floor(min.x.getTime() / params.timelineProp.xFloorScale) * params.timelineProp.xFloorScale);
    }
  }
  if (max.x && max.x.getTime() !== 0) {
    if (params.timelineProp?.xFloorConst) {
      max.x = new Date(max.x.getTime() + params.timelineProp.xFloorConst);
    }
    if (params.timelineProp?.xFloorScale) {
      max.x = new Date(Math.ceil(max.x.getTime() / params.timelineProp.xFloorScale) * params.timelineProp.xFloorScale);
    }
  }
  if (min.y) {
    if (params.timelineProp?.yFloorConst) {
      min.y = min.y - params.timelineProp.yFloorConst;
    }
    if (params.timelineProp?.yFloorScale) {
      min.y =
        params.timelineProp?.min?.y !== undefined && params.timelineProp?.min?.y < min.y
          ? params.timelineProp?.min?.y
          : Math.floor(min.y / params.timelineProp.yFloorScale - 0.1) * params.timelineProp.yFloorScale;
    }
  }
  if (max.y) {
    if (params.timelineProp?.yFloorConst) {
      max.y = max.y + params.timelineProp.yFloorConst;
    }
    if (params.timelineProp?.yFloorScale) {
      max.y =
        params.timelineProp?.max?.y !== undefined && params.timelineProp.max.y > max.y
          ? params.timelineProp?.max?.y
          : Math.ceil(max.y / params.timelineProp.yFloorScale + 0.1) * params.timelineProp.yFloorScale;
    }
  }
  if (data.length > 0) {
    if (notChange) notChange = min.x?.valueOf() === (org?.min.x ? org.min.x.valueOf() : undefined);
    if (notChange) notChange = min.y === org?.min.y;
    if (notChange) notChange = max.x?.valueOf() === (org?.max.x ? org.max.x.valueOf() : undefined);
    if (notChange) notChange = max.y === org?.max.y;
    // if (!notChange) {
    //   Debug("min max changed");
    // }

    if (notChange) {
      if (checkPoint && org?.checkPoint) {
        const keys = Object.keys(checkPoint);
        const orgKeys = Object.keys(org?.checkPoint ?? {});
        if (notChange) notChange = keys.length === orgKeys.length;
        if (notChange) {
          for (let key in keys) {
            notChange =
              (checkPoint[key] ? checkPoint[key].valueOf() : undefined) ===
              (org.checkPoint[key] ? org.checkPoint[key].valueOf() : undefined);
            if (!notChange) break;
          }
        }
      } else {
        notChange = checkPoint === org?.checkPoint;
      }
    }
    // if (!notChange) {
    //   Debug("checkPoint changed");
    // }

    if (notChange) {
      if (checkValue && org?.checkValue) {
        if (notChange) notChange = checkValue.length === org.checkValue.length;
        if (notChange) {
          for (let i = 0; i < checkValue.length; i++) {
            notChange = checkValue[i] === org.checkValue[i];
            if (!notChange) break;
          }
        }
      } else {
        notChange = checkValue === org?.checkValue;
      }
    }
    // if (!notChange) {
    //   Debug("checkValue changed");
    // }

    if (notChange && org?.data) {
      notChange = data.length === (org.data.length ?? 0);
      if (notChange) {
        for (let i = 0; i < data.length; i++) {
          const tempData = data[i];
          const orgData = org.data[i];
          notChange = tempData.id === orgData.id && tempData.data.length === orgData.data.length;
          if (notChange) {
            for (let j = 0; j < tempData.data.length; j++) {
              notChange =
                (tempData.data[j].x ? tempData.data[j].x?.valueOf() : undefined) ===
                  (orgData.data[j].x ? orgData.data[j].x?.valueOf() : undefined) && tempData.data[j].y === orgData.data[j].y;
              if (!notChange) break;
            }
          }
          if (!notChange) break;
        }
      }
    } else {
      notChange = !data && !org?.data;
    }
    if (operationKeys && operationProp) {
      const operationSeriesList: TimeLineSeries[] = [];
      const timelineSeriesMap: Record<string, TimeLineSeries> = {};
      data.forEach((s) => {
        timelineSeriesMap[s.id] = s;
      });
      operationKeys?.forEach((key) => {
        const tempProp = operationProp[key];
        const dataA: TimeLineSeries | undefined = tempProp ? timelineSeriesMap[tempProp.dataA] : undefined;
        const dataB: TimeLineSeries | undefined = tempProp && tempProp.dataB ? timelineSeriesMap[tempProp.dataB] : undefined;
        const operationId = tempProp.id ?? key;
        const operationSeries: TimeLineSeries = { id: operationId, data: [] };
        let startIndex: number = 0;
        const operationShift = cbt && tempProp.compareStr ? cbt.compare[tempProp.compareStr] : undefined;
        const operationStart = tempProp.startDate ? (tempProp.startDate?.valueOf() as number) + (operationShift ?? 0) : undefined;
        const operationEnd = tempProp.endDate ? (tempProp.endDate?.valueOf() as number) + (operationShift ?? 0) : undefined;
        const operationScale = tempProp.scale ?? 1;
        const miny = min.y ?? 0;

        dataA?.data.forEach((tempData, index) => {
          const valueA = tempData.y;
          const timeA = tempData.x ? Number(tempData.x?.valueOf()) : undefined;
          if (
            valueA !== null &&
            timeA !== null &&
            timeA !== undefined &&
            (operationStart === undefined || (operationStart && operationStart <= timeA)) &&
            (operationEnd === undefined || (operationEnd && operationEnd >= timeA))
          ) {
            for (let i = startIndex; i < dataA.data.length; i++) {
              const tempDataB = dataB?.data ? dataB?.data[i] : undefined;
              const timeB = tempDataB ? Number(tempDataB.x?.valueOf()) : undefined;
              if (timeB) {
                if (timeA === timeB || (timeA - 500 < timeB && timeA + 500 > timeB)) {
                  const valueB = tempDataB?.y;
                  let result: number | undefined = undefined;
                  if (tempProp.operation instanceof Function) {
                    result = tempProp.operation(valueA, valueB);
                  } else if (valueB !== null && valueB !== undefined) {
                    switch (tempProp.operation) {
                      case "ADD":
                        result = valueA + valueB;
                        break;
                      case "SUB":
                        result = valueA - valueB;
                        break;
                      case "MUL":
                        result = valueA * valueB;
                        break;
                      case "DIV":
                        result = valueA / valueB;
                        break;
                      case "AVG":
                        result = valueA + valueB / 2;
                        break;
                      case "MIN":
                        result = Math.min(valueA, valueB);
                        break;
                      case "MAX":
                        result = Math.max(valueA, valueB);
                        break;
                      case "DIFF_ABS":
                        result = Math.abs(valueA - valueB);
                        break;
                      case "DIFF_REL":
                        result = (valueA - valueB) / valueB;
                        break;
                    }
                  }
                  if (result !== undefined) {
                    operationSeries.data.push({ x: tempData.x, y: result * operationScale + miny });
                  }
                  startIndex = i + 1;
                  break;
                }
                if (timeA + 500 < timeB) {
                  break;
                }
              }
            }
          }
        });
        Debug(operationSeries);
        if (!checkEmpty(operationSeries.data)) {
          operationSeriesList.push(operationSeries);
          if (seriesScale) {
            seriesScale[operationId] = { scale: operationScale, yFloorConst: miny, toFixed: tempProp.toFixed };
          }
        }
      });
      if (!checkEmpty(operationSeriesList)) {
        data.push(...operationSeriesList);
      }
    }
    if (rorKeys && rorProp) {
      const rorSeriesList: TimeLineSeries[] = [];
      data.forEach((s) => {
        if (typeof s.id === "string" && rorKeys.includes(s.id)) {
          const ror = rorProp[s.id];
          const rorId = ror.rorId ?? s.id;
          const rorSeries: TimeLineSeries = { id: rorId, data: [] };
          const rorTerm = (ror.rorTerm ?? 30) * 1000;
          const rorTermUnit = (ror.rorTermUnit ?? 60) * 1000;
          const rorShift = cbt && ror.compareStr ? cbt.compare[ror.compareStr] : undefined;
          const rorStart = (ror.startDate?.valueOf() as number) + (rorShift ?? 0);
          const rorEnd = (ror.endDate?.valueOf() as number) + (rorShift ?? 0);
          const rorScale = ror.rorScale ?? 1;
          const miny = min.y ?? 0;
          let startIndex: number = 0;
          s.data.forEach((tempData, index) => {
            const tempX = tempData.x?.valueOf() as number;
            // Debug(tempData, index, tempData.x, ror.startDate, ror.endDate);
            if (tempX && rorStart && rorStart <= tempX && rorEnd && rorEnd >= tempX) {
              for (let i = startIndex; i < index; i++) {
                const checkData = s.data[i];
                const checkX = checkData && checkData.x !== null ? (checkData.x.valueOf() as number) : undefined;
                if (checkX && tempX - checkX <= rorTerm) {
                  startIndex = i - 1;
                  if (tempData.y && checkData.y) {
                    const rorValue =
                      Math.round(
                        (((tempData.y - checkData.y) * (tempX - checkX)) / rorTerm) * (rorTermUnit / rorTerm) * 10 ** (ror.toFixed ?? 0),
                      ) /
                      10 ** (ror.toFixed ?? 0);
                    if (rorValue >= 0) {
                      rorSeries.data.push({ x: tempData.x, y: rorValue * rorScale + miny });
                    }
                  }
                  break;
                }
              }
            }
          });
          // Debug(rorSeries);
          if (!checkEmpty(rorSeries.data)) {
            rorSeriesList.push(rorSeries);
            if (seriesScale) {
              seriesScale[rorId] = { scale: rorScale, yFloorConst: miny, toFixed: ror.toFixed };
            }
          }
        }
      });
      // Debug(rorSeriesList);
      if (!checkEmpty(rorSeriesList)) {
        data.push(...rorSeriesList);
      }
    }
    // if (!notChange) {
    //   Debug("data changed");
    // } else {
    //   Debug("!data changed");
    // }
    Debug("data : ", data, min, max, checkValue);
    // Debug("point : ", checkPoint, comparePoint);

    return { data, min, max, valueMax, checkPoint, comparePoint, checkValue, tempDataTitle, seriesScale, notChange };
  }
  return undefined;
};
const dataChanged = (
  t: TransitionType,
  org: TimeLineData,
  setData: Dispatch<SetStateAction<TimeLineData>>,
  params: DataInputParams,
  e: any,
  lineTimeout: RefObject<NodeJS.Timeout | undefined>,
) => {
  const active: HTMLInputElement | undefined = document?.activeElement as HTMLInputElement;
  const activeId = active.tagName == "INPUT" ? active.id : undefined;
  let cnt = 0;
  const times = params.timelineProp?.showTimes ? Object.keys(params.timelineProp?.showTimes) : undefined;
  //  Debug(e, times);

  const checkActiveTimeout = () => {
    cnt++;
    const nowActive: HTMLInputElement | undefined = document?.activeElement as HTMLInputElement;
    const nowActiveId = nowActive.tagName == "INPUT" ? nowActive.id : undefined;
    const changed = params.form?.getFieldValue(params.name);
    if (changed) {
      if (!activeId || (activeId && (nowActiveId !== activeId || (times && times.includes(activeId)))) || cnt > 7) {
        //Debug("setData : check Data Change start");
        const convert = dataConverter(t, org, params);
        if (convert) {
          if (params.form) {
            params.form.setFieldValue(params.name, false);
            if (!convert.notChange) {
              // Debug("setData : Data Change", convert);
              setData(convert);
            } else {
              // Debug("setData : Data not Change");
            }
          }
        }
      } else {
        if (lineTimeout.current) {
          clearTimeout(lineTimeout.current);
        }
        lineTimeout.current = setTimeout(checkActiveTimeout, dc.setTimeoutDelay);
      }
    }
  };

  if (lineTimeout.current) {
    clearTimeout(lineTimeout.current);
  }
  lineTimeout.current = setTimeout(checkActiveTimeout, 50);
};

const customSliceTooltip = (
  params: DataTimelineType,
  p: SliceTooltipProps<LineSeries>,
  checkPoint: any,
  comparePoint: Record<string, Record<number, string>>,
) => {
  // Debug(p, checkPoint, comparePoint);
  const time_str = p.slice.points[0].data.x;
  if (!time_str) {
    return undefined;
  }
  const time = Math.floor(new Date(time_str).getTime() * 1000) / 1000;
  const check = checkPoint[time];
  const compare: { check: string; term: string }[] = [];
  Object.keys(comparePoint).forEach((check) => {
    if (comparePoint[check][time]) {
      compare.push({ check, term: comparePoint[check][time] });
    }
  });
  // if (!check) return undefined;
  const trs = p.slice.points
    .map((i, index) => {
      const id = i.seriesId as string;
      // const sereeisUnit = params
      const seriesUnit = params.ySeriesUnit ? params.ySeriesUnit[id] : undefined;
      const serieScale = params.seriesScale ? params.seriesScale[id] : undefined;
      return checkLimit(params, i.data.y, id) ? (
        <tr key={"sliceTooltip_" + index}>
          <td style={{ padding: "3px 5px" }}>
            <span style={{ display: "block", width: "1rem", height: "1rem", background: i.color, marginRight: "0.5rem" }}></span>
          </td>
          <td style={{ padding: "3px 5px" }}>
            {params.t(id)}
            {params.tempDataTitle[id] ? ":" + params.tempDataTitle[id] : ""}
          </td>
          <td style={{ padding: "3px 5px" }}>
            <span style={{ fontWeight: "bold" }}>
              {seriesUnit || (serieScale && serieScale.scale)
                ? (serieScale && serieScale.scale && typeof i.data.y === "number"
                    ? Math.round(((i.data.y - (serieScale.yFloorConst ?? 0)) / serieScale.scale) * 10 ** (serieScale.toFixed ?? 0)) /
                      10 ** (serieScale.toFixed ?? 0)
                    : i.data.y) + (seriesUnit ?? "")
                : i.data.yFormatted}
            </span>
          </td>
        </tr>
      ) : undefined;
    })
    .filter((item) => item !== undefined);
  return trs.length > 0 ? (
    <div style={dc.chartCoverDivstyle}>
      <div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td></td>
              <td style={{ padding: "3px 5px", textAlign: "center" }}>
                <strong>{check ? check : compare.length > 0 ? compare.map((item) => item.term).join(",") : ""}</strong>
              </td>
              <td>
                <strong>
                  {time >= 0
                    ? dayjs(time).add(-utcOffset).format(timeformat)
                    : "-" + dayjs(Math.abs(time)).add(-utcOffset).format(timeformat)}
                </strong>
              </td>
            </tr>
            {trs}
          </tbody>
        </table>
      </div>
    </div>
  ) : undefined;
};
const lineXStyle = {
  stroke: "var(--gray-11)",
  strokeWidth: 1,
  strokeOpacity: 1,
};
const lineXDashStyle = {
  stroke: "var(--gray-11)",
  strokeDasharray: "6,6",
  strokeDashoffset: -6,
  strokeWidth: 1,
  strokeOpacity: 0.3,
};
const lineYStyle = {
  stroke: "var(--gray-11)",
  strokeWidth: 1,
  strokeOpacity: 1,
};
const lineYDashStyle = {
  stroke: "var(--gray-11)",
  strokeDasharray: "8,4",
  strokeWidth: 1,
  strokeOpacity: 0.3,
};
type DataTimelineType = TimeLineProps & {
  t: TransitionType;
  locale: string | undefined;
  xMax: Date | undefined;
  valueMax: TimeLineXY | undefined;
  xLimit: boolean;
  chartShow: boolean;
  startTerm: number;
  compare: boolean;
  tempDataTitle: Record<string, string>;
  seriesScale: SeriesScaleRecord | undefined;
  setStartTime: Dispatch<SetStateAction<Date | undefined>>;
};
export function DataTimelineItem(params: DataTimelineType) {
  const checkIdArr = (params.checkIds === undefined ? [] : typeof params.checkIds === "string" ? [params.checkIds] : params.checkIds).map(
    (item) => {
      return (params.transitionId ?? false) ? params.t(item) : item;
    },
  );
  const hintIdArr = (params.hintIds === undefined ? [] : typeof params.hintIds === "string" ? [params.hintIds] : params.hintIds).map(
    (item) => {
      return (params.transitionId ?? false) ? params.t(item) : item;
    },
  );
  const hintIdPoint: Record<string, number> = {};

  const customTooltip: PointTooltipComponent<LineSeries> = (a: PointTooltipProps<LineSeries>) => {
    const time_str = a.point.data.x;
    if (!time_str) {
      return undefined;
    }
    const time = new Date(time_str).getTime();
    const str = time >= 0 ? dayjs(time).add(-utcOffset).format(timeformat) : "-" + dayjs(Math.abs(time)).add(-utcOffset).format(timeformat);
    return (
      <div style={dc.chartCoverDivstyle}>
        <div style={{ whiteSpace: "pre", display: "flex", alignItems: "center" }}>
          <span
            style={{
              display: "block",
              width: "1rem",
              height: "1rem",
              background: a.point.seriesColor,
              marginRight: "0.5rem",
            }}
          ></span>
          <span style={{ color: a.point.seriesColor }}>
            <strong>{params.t(a.point.seriesId as string)}</strong>
          </span>
          <span style={{ width: "1rem", textAlign: "center", color: a.point.seriesColor }}>:</span>
          <span>
            <strong>{str}</strong> - <strong>{a.point.data.yFormatted}</strong>
          </span>
        </div>
      </div>
    );
  };

  const checkPoint: Record<number, string> = {};

  const paramsCheckPoint = params.checkPoint;
  const baseTime = params.baseTime && paramsCheckPoint && paramsCheckPoint[params.baseTime] !== undefined ? params.baseTime : undefined;
  const baseTimeValue =
    params.baseTime && paramsCheckPoint && paramsCheckPoint[params.baseTime] !== undefined ? paramsCheckPoint[params.baseTime] : undefined;
  if (params.max?.x) {
    if ((baseTimeValue !== undefined && params.max.x.getTime() !== baseTimeValue.getTime()) || baseTimeValue === undefined) {
      checkPoint[params.max.x.getTime()] = params.xMaxText ?? params.t("blank");
    }
  }
  if (params.min?.x) {
    if ((baseTimeValue !== undefined && params.min.x.getTime() !== baseTimeValue.getTime()) || baseTimeValue === undefined) {
      checkPoint[params.min.x.getTime()] = params.xMinText ?? params.t("blank");
    }
  }
  if (baseTime !== undefined && baseTimeValue !== undefined) {
    checkPoint[baseTimeValue.getTime()] = params.t(baseTime);
  }

  //가로 점선위치
  const checkDashPoint: Record<number, string> = {};
  const checkTimes = params.checkTimes;
  if (paramsCheckPoint) {
    //Debug(paramsCheckPoint, checkTimes);
    Object.keys(paramsCheckPoint).forEach((item) => {
      if (checkTimes) {
        if (checkTimes.includes(item)) {
          checkDashPoint[paramsCheckPoint[item].getTime()] = params.t(item);
        }
      } else {
        checkDashPoint[paramsCheckPoint[item].getTime()] = params.t(item);
      }
    });
  }
  const comparePoint: Record<string, Record<number, string>> = {};
  const comparePointFloor: Record<string, Record<number, string>> = {};
  const compareAllFloor: Record<string, number[]> = {};
  const paramsCompare = params.comparePoint;
  if (paramsCompare && checkTimes) {
    Object.keys(paramsCompare).forEach((check) => {
      Object.keys(paramsCompare[check]).forEach((term) => {
        if (paramsCompare[check][term] && checkTimes.includes(term)) {
          const time = paramsCompare[check][term].getTime();
          if (term !== baseTime || (baseTimeValue !== undefined && time !== baseTimeValue.valueOf())) {
            const timeFloor = Math.floor(time / 1000) * 1000;
            if (!comparePoint[check]) {
              comparePoint[check] = {};
            }
            if (!comparePointFloor[check]) {
              comparePointFloor[check] = {};
            }
            if (!compareAllFloor[check]) {
              compareAllFloor[check] = [];
            }
            comparePoint[check][time] = params.t(term);
            comparePointFloor[check][timeFloor] = params.t(term);
            compareAllFloor[check].push(timeFloor);
          }
        }
      });
    });
  }

  //checkPoint -> checkAllPoint
  //점 위치 입력
  const checkAllPoint: Record<number, string> = { ...checkPoint };
  Object.keys(checkDashPoint).forEach((key) => {
    const keyno = Number(key);
    if (!checkAllPoint[keyno]) {
      checkAllPoint[keyno] = checkDashPoint[keyno];
    }
  });
  const checkAllPointFloor: Record<number, string> = {};
  Object.keys(checkAllPoint).forEach((key) => {
    const num = Number(key);
    const floor = Math.floor(num / 1000) * 1000;
    checkAllPointFloor[floor] = checkAllPoint[num];
  });
  const checkArr = Object.keys(checkPoint)
    .map((key) => Number(key))
    .sort();

  const checkDashArr = Object.keys(checkDashPoint).map((key) => Number(key));
  const checkAllArr = [...checkArr].sort();
  checkDashArr.forEach((key) => {
    if (!checkAllArr.includes(key)) checkAllArr.push(key);
  });
  checkAllArr.sort();
  const checkAllFloor = checkAllArr.map((item) => {
    return Math.floor(item / 1000) * 1000;
  });
  const markerArr: { axis: "x" | "y"; value: any; style: Partial<CSSProperties> }[] = checkArr.map((item) => {
    return { axis: "x", value: item, style: lineXStyle };
  });
  checkDashArr.forEach((item) => {
    markerArr.push({ axis: "x", value: item, style: lineXDashStyle });
  });
  const yMarker: number[] = [params.min?.y ?? 100, params.max?.y ?? 250];
  const yDashMarker: number[] = params.checkValuePoint ?? [200];
  yMarker.forEach((item) => markerArr.push({ axis: "y", value: item, style: lineYStyle }));
  yDashMarker.forEach((item) => markerArr.push({ axis: "y", value: item, style: lineYDashStyle }));
  markerArr.sort();
  const yTickValues: number[] | undefined =
    params.min?.y !== undefined && params.max?.y !== undefined && params.yFloorScale !== undefined ? [] : undefined;
  const yGridvalues = [];
  if (yTickValues && params.min?.y !== undefined && params.max?.y !== undefined && params.yFloorScale !== undefined) {
    const yDiffCountTerm = params.yGridCount ? Math.ceil((params.max?.y - params.min?.y) / params.yFloorScale / params.yGridCount) : 1;
    let cnt = 0;
    for (let i = params.min?.y; i <= params.max?.y; i += params.yFloorScale) {
      if (cnt % yDiffCountTerm === 0 || params.min?.y === i || params.max?.y === i || yDashMarker.includes(i)) {
        yTickValues.push(i);
      }
      if (params.min?.y !== i && params.max?.y !== i && !yDashMarker.includes(i)) {
        yGridvalues.push(i);
      }
      cnt++;
    }
  }
  const colorMap = params.colorMap;

  // let active: HTMLInputElement | undefined = undefined;
  // try {
  //   active = document?.activeElement as HTMLInputElement;
  // } catch (e) {}
  // const activeId = active ? active.id : undefined;
  // const mounted = useRef(false);
  // useEffect(() => {
  //   if (!mounted.current) {
  //     mounted.current = true;
  //   } else {
  //     const activeObj = activeId ? document.getElementById(activeId) : null;
  //     if (activeObj && isInViewport(activeObj)) {
  //       //activeObj.focus();
  //     }
  //     Debug("componentDidUpdate[Function]");
  //   }
  // });
  //Debug(params.min, params.max);
  // Debug("baseTime : ", baseTime, baseTimeValue);
  // Debug("check : ", checkAllArr, checkAllPoint);
  // Debug("compare : ", compareAllFloor);

  const data: readonly LineSeries[] = params.data ?? defaultTimelineData;
  // Debug(data, checkPoint, params.comparePoint, comparePoint, comparePointFloor);
  if (!params.chartShow) {
    return undefined;
  }
  return (
    <ResponsiveLine
      theme={nivoTheme}
      onClick={params.onClick}
      animate={params.isAnimation}
      data={!params.compare && !checkEmpty(params.checkIds) ? data.filter((s) => params.checkIds?.includes(s.id as string)) : data}
      axisBottom={{
        format: (a) => {
          // Debug(dayjs(a).valueOf());
          return !(params.xBottomTickZeroShow ?? true) && (a === 0 || dayjs(a).valueOf() === 0)
            ? ""
            : a >= 0
              ? dayjs(a).add(-utcOffset).format(timeformat)
              : "-" + dayjs(Math.abs(a)).add(-utcOffset).format(timeformat);
        },
        tickValues: params.xBottomTickValues
          ? params.xBottomTickValues === "checkAll"
            ? checkAllArr
            : params.xBottomTickValues
          : params.isMobile
            ? "every 4 minutes"
            : "every 2 minutes",
      }}
      axisTop={{
        renderTick: params.xTopShowTime
          ? (props) => {
              // Debug(props.value);
              const label_str =
                props.value === 0 ||
                dayjs(props.value).valueOf() === 0 ||
                (params.valueMax?.x !== undefined &&
                  dayjs(params.xMax).valueOf() !== dayjs(params.valueMax?.x).valueOf() &&
                  dayjs(props.value).valueOf() === dayjs(params.xMax).valueOf())
                  ? ""
                  : props.value > 0
                    ? getDayjsValueStrCommon(
                        dayjs(props.value).add(-utcOffset),
                        "time",
                        undefined,
                        dc.getSecondTimerFormat(params.locale, "\r\n"),
                      ) + ""
                    : "-" +
                      getDayjsValueStrCommon(
                        dayjs(Math.abs(props.value)).add(-utcOffset),
                        "time",
                        undefined,
                        dc.getSecondTimerFormat(params.locale, "\r\n"),
                      );
              const labels = label_str.split("\r\n");
              const dy = labels.length > 1 ? (labels.length - 1) * (1 * 0.5) - (checkSafari ? 1 * 0.4 : 0) : undefined;
              const fontSize = params.isMobile ? 8 : 11 * (labels.length > 1 && params.isMobile ? 0.8 : 1);
              return (
                <g transform={`translate(${props.x}, ${props.y})`}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="var(--gray-a11)"
                    fontFamily="sans-serif"
                    fontSize={fontSize + "px"}
                  >
                    {labels.length < 2 ? (
                      <tspan key={props.tickIndex + "_0"} y={-fontSize}>
                        {label_str}
                      </tspan>
                    ) : (
                      labels.map((label: any, index: number) => {
                        return (
                          <tspan
                            key={props.tickIndex + "_" + index}
                            x={dy ? 0 : undefined}
                            y={dy ? (index - dy) * fontSize - fontSize / 2 : undefined}
                          >
                            {label}
                          </tspan>
                        );
                      })
                    )}
                  </text>
                </g>
              );
            }
          : undefined,
        // params.xTopShowTime
        // ? : undefined,
        format: params.xTopShowTime
          ? undefined
          : (a) => {
              // Debug(a);
              const prevStr = checkAllPoint[a];
              if (prevStr === "blank" || prevStr === undefined) {
                return "";
              } else {
                return prevStr;
              }
            },
        tickValues: checkAllArr,
      }}
      axisLeft={{
        legendOffset: 12,
        format: (a) => {
          return a + params.yUnit;
        },
        tickValues: yTickValues,
        //tickValues: [0, 50, 100, 150, 200, 250],
      }}
      curve="monotoneX" //"step" | "linear" | "monotoneX" | "basis" | "cardinal" | "catmullRom" | "monotoneY" | "natural" | "stepAfter" | "stepBefore"
      colors={
        colorMap !== undefined
          ? ({ id }) => {
              return (colorMap[id + ""] ? colorMap[id + ""].color : undefined) ?? "#aeaeae";
            }
          : params.colors
      }
      //enablePointLabel
      enableTouchCrosshair
      enableSlices="x"
      margin={{
        bottom: 30,
        left: 50,
        right: 20,
        top: 30,
      }}
      sliceTooltip={(p) => {
        return customSliceTooltip(params, p, checkAllPointFloor, comparePointFloor);
      }}
      tooltip={customTooltip}
      enablePointLabel
      pointLabelYOffset={24}
      pointBorderColor={{
        from: "color",
        modifiers: [["darker", 0.3]],
      }}
      pointBorderWidth={1}
      pointSize={8}
      pointSymbol={(p) => {
        if (p.datum.x) {
          const check = dayjs(p.datum.x).valueOf();
          const id = p.datum.seriesId as string;
          const checkId = params.checkIds?.includes(id);
          const compareId = (
            checkId || !id
              ? [""]
              : Object.keys(compareAllFloor)
                  .map((key) => (id.indexOf(key) >= 0 ? key : undefined))
                  .filter((item) => item !== undefined)
          )[0];
          if (
            ((checkId && checkAllFloor.includes(check)) ||
              (!checkEmpty(compareId) && compareAllFloor[compareId] && compareAllFloor[compareId].includes(check))) &&
            checkLimit(params, p.datum.y, id)
          ) {
            // Debug(compareAllFloor);
            return (
              <circle
                r={p.size / 2}
                fill={p.color}
                stroke={"rgb(208, 173, 144)"}
                strokeWidth={p.borderWidth}
                style={{ pointerEvents: "none" }}
              ></circle>
            );
          } else {
            return undefined;
          }
        }
      }}
      pointLabel={(d) => {
        const id = d.seriesId as string;
        const xValue = dayjs(d.data.x).valueOf();
        if (
          checkLimit(params, d.data.y, id) &&
          ((checkIdArr.includes(id) && checkAllFloor.includes(xValue)) ||
            (params.startTerm > 0 && params.startTerm <= xValue && hintIdArr.includes(id) && !Object.keys(hintIdPoint).includes(id)))
        ) {
          if (params.startTerm > 0 && params.startTerm <= xValue && hintIdArr.includes(id) && !Object.keys(hintIdPoint).includes(id)) {
            // Debug(id + "," + xValue + "," + params.startTerm);
            hintIdPoint[id] = xValue;
          }

          const seriesUnit = params.ySeriesUnit ? params.ySeriesUnit[id] : undefined;
          const serieScale = params.seriesScale ? params.seriesScale[id] : undefined;
          return seriesUnit || (serieScale && serieScale.scale)
            ? (serieScale && serieScale.scale && typeof d.data.y === "number"
                ? Math.round(((d.data.y - (serieScale.yFloorConst ?? 0)) / serieScale.scale) * 10 ** (serieScale.toFixed ?? 0)) /
                  10 ** (serieScale.toFixed ?? 0)
                : d.data.y) + (seriesUnit ?? "")
            : d.data.yFormatted + "";
        } else {
          return "";
        }
      }}
      useMesh
      xFormat="time:%M:%S"
      xScale={{
        format: "%M:%S",
        precision: "second",
        type: "time",
        useUTC: true,
        min: params.min?.x,
        max: params.max?.x,
      }}
      // xScale={{
      //   format: "%H:%M:%S",
      //   precision: "millisecond",
      //   type: "time",
      //   useUTC: true,
      //   min: params.min?.x,
      //   max: params.max?.x,
      // }}
      yFormat={(a) => {
        return typeof a === "number" ? Math.round(a * 10) / 10 + (params?.yUnit ?? "") : a + (params?.yUnit ?? "");
      }}
      yScale={{
        type: "linear",
        min: params.min?.y,
        max: params.max?.y,
        clamp: true,
      }}
      enableArea
      areaOpacity={0.3}
      areaBaselineValue={params.min?.y}
      defs={timelineDefs}
      fill={params.fill}
      markers={markerArr.map((check) => {
        const result: CartesianMarkerProps<DatumValue> = {
          axis: check.axis,
          //legend: checkPoint[check],
          lineStyle: check.style,
          value: check.value,
        };
        return result;
      })}
      gridYValues={yGridvalues}
      layers={[
        "grid",
        "markers",
        "axes",
        "areas",
        ({ series, lineGenerator }) => CustomLines({ series, lineGenerator, colorMap }),
        // "lines",
        "points",
        (props) => <MovingVerticalBar {...props} startTerm={params.startTerm} fillColor={params.timerColor} xMax={params.xMax} />,
        "slices",
        "legends",
        "crosshair",
        "mesh",
      ]}
      //motionConfig="wobbly"
      // motionConfig={{
      //   mass: 72,
      //   tension: 170,
      //   friction: 26,
      //   clamp: true,
      //   precision: 0.01,
      //   velocity: 0,
      // }}
    />
  );
}
export default function DataTimeLine(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const ta = useTranslations("alert");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const dataLabel: string | DataElement = params.label ?? (params.labelNamePrev ?? "") + params.name;
  const DataLabel: DataElement =
    typeof params.label === "string" || params.label === undefined
      ? { element: params.label ?? (params.labelNamePrev ?? "") + params.name }
      : params.label;
  const [data, setData] = useState<TimeLineData>(undefined);
  if (params.resetFunction && params.name) {
    params.resetFunction[params.name] = () => {
      setData(dataConverter(t, data, params));
    };
  }
  const layoutRef = useRef<HTMLDivElement>(null);
  const alertContext = useContext(AlertContext);
  const [isMobile, setMobile] = useState<boolean | undefined>(checkMobile());
  const [isAnimation, setAnimation] = useState(params.timelineProp?.isAnimation ?? true);
  const lineTimeout = useRef<NodeJS.Timeout>(undefined);
  const [chartShow, setChartShow] = useState(true);
  const [fixed, setFixed] = useState(params.timelineProp?.fixed ?? false);
  const [fixedStore, setFixedStore] = useState(params.timelineProp?.fixed ?? false);
  const [compare, setCompare] = useState(false);
  const [startTime, setStartTime] = useState<Date | undefined>(new Date());
  const [startTerm, setStartTerm] = useState(-1);

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    const temp_function = params.formFunction[params.name];
    if (temp_function) {
      temp_function["setFixed"] = (e: any) => {
        setFixed(e);
      };
      temp_function["getFixed"] = (e: any) => {
        return fixed;
      };
      temp_function["setStartTime"] = (e: any) => {
        setStartTime(e);
      };
      temp_function["setCompare"] = (e: any) => {
        setCompare(e);
      };
    }
  }

  useEffect(() => {
    setFixed(params.timelineProp?.fixed ?? false);
    setStartTime(undefined);
    setStartTerm(-1);
  }, [params.inputMode]);

  useEffect(() => {
    if (!startTime) {
      setStartTerm(-1);
      if (params?.setTimer && params.name) {
        params.setTimer({ timer: params.indexKey, start: undefined, chartTimerClear: false });
      }
      return;
    }

    const id = startTime
      ? setInterval(
          () => {
            setStartTerm((prev) => dayjs().diff(startTime));
          },
          params.timelineProp?.timerDelay ?? dc.setTimeoutShort * 3,
        )
      : undefined;

    return () => {
      if (id) {
        clearInterval(id);
      }
      return;
    };
  }, [startTime]);

  const updateScroll = useCallback(
    (e: any) => {
      const layout = layoutRef.current;
      if (fixed && layout) {
        const header = document.querySelector("body:not(.menuBottom) header.mainHeader > div") as HTMLElement;
        const headerFull = document.querySelector("body.screenFull header.mainHeader > div") as HTMLElement;
        const tool = document.querySelector(".mainTool > div > div.fixed") as HTMLElement;
        const toolTop = tool?.getBoundingClientRect().top ?? 0;
        const toolMargin = (tool ? tool.getBoundingClientRect().top + tool.getBoundingClientRect().height : 0) + 4;

        const main = document.querySelector("body:not(.menuBottom) main.mainGroup") as HTMLElement;
        // Debug(header ? header.offsetHeight : 0, headerFull ? headerFull.offsetHeight : 0, tool ? tool.offsetHeight : 0);

        const layoutParent = layout.parentElement;
        const chartOffset = toolMargin;
        const scrollAmount = document.documentElement.scrollTop;
        const toolConst = 32;
        const fullConst = 0; //headerFull ? -112 : 0;
        const diff =
          scrollAmount -
          (layoutParent ? layoutParent.offsetTop : 0) -
          (header ? (main ? main.offsetTop - header.offsetHeight : 0) : tool ? tool.offsetHeight + toolConst : 0) +
          toolTop -
          4;
        // Debug(scrollAmount);
        if (diff < fullConst) {
          if (layout.classList.contains("fixed-chart")) {
            layout.classList.remove("fixed-chart");
            layout.style.removeProperty("width");
            layout.style.removeProperty("height");
            //layout.style.removeProperty("margin-left");
            layout.style.removeProperty("top");
          }
        } else {
          layout.style.top = chartOffset + "px";
          if (!layout.classList.contains("fixed-chart")) {
            if (layout.parentElement) {
              layout.style.width = layout.parentElement.offsetWidth + "px";
              layout.style.height = layout.parentElement.offsetHeight + "px";
              if (isMobile) {
                layout.style.height = (layout.parentElement.offsetHeight * 2) / 3 + "px";
              }
            }
            layout.classList.add("fixed-chart");
          } else {
            const parentHeight = layoutParent?.offsetHeight ?? 0;
            const calHeight = parentHeight - diff + (header ? 0 : tool ? tool.offsetHeight - toolConst : 0) + fullConst;
            const scale = isMobile ? 1.25 : 1.9;
            const height = calHeight > parentHeight ? parentHeight : calHeight > parentHeight / scale ? calHeight : parentHeight / scale;

            if (layout.parentElement) {
              layout.style.width = layout.parentElement.offsetWidth + "px";
            }
            if (layout.offsetHeight !== height) {
              layout.style.height = height + "px";
            }
          }
        }
      }
    },
    [params, isMobile, fixed],
  );
  useEffect(() => {
    let now_mobile = isMobile;
    let width = 0;
    let updateTimer: NodeJS.Timeout | undefined = undefined;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target.tagName === "BODY") {
          if (width !== entry.contentRect.width) {
            width = entry.contentRect.width;
            const check_mobile = checkMobile();
            if (now_mobile !== check_mobile) {
              //now_mobile = check_mobile;
              setMobile(() => check_mobile);
            }
            if (updateTimer) {
              clearTimeout(updateTimer);
            }
            updateTimer = setTimeout(() => {
              updateScroll(null);
            }, dc.setTimeoutDelay);
          }
        }
      });
    });

    if (document.body) {
      resizeObserver.observe(document.body);
    }
    if (!fixed) {
      const layout = layoutRef.current;
      if (layout && layout.classList.contains("fixed-chart")) {
        layout.classList.remove("fixed-chart");
        layout.style.removeProperty("width");
        layout.style.removeProperty("height");
        //layout.style.removeProperty("margin-left");
        layout.style.removeProperty("top");
      }
    } else {
      updateScroll(null);
    }
    window.addEventListener("scroll", updateScroll);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updateScroll);
    };
  }, [updateScroll]);

  const watch = Form.useWatch(params.name, params.form);
  if (watch) {
    dataChanged(t, data, setData, params, { key: params.name, value: true }, lineTimeout);
  }

  useEffect(() => {
    const convert = dataConverter(t, undefined, params);
    if (convert) {
      setData(convert);
    }
  }, [t, params]);

  const GetTimelineInputs = (params: DataInputParams) => {
    if (params.fieldId && typeof params.fieldId !== "string") {
      const fileds = Object.keys(params.fieldId);
      return (
        <>
          <DataInput {...params} inputType="switch" defaultValue={false} itemClassName="hidden" noItem={false} />;
          {fileds.map((field, index) => {
            if (params.fieldId && typeof params.fieldId !== "string" && params.fieldId[field]) {
              const fieldProp: DataInputParams | undefined = {
                indexKey: params.indexKey,
                values: params.values,
                form: params.form,
                fieldId: params.fieldId[field].fieldId ?? field,
                loading: params.loading,
                inputMode: params.inputMode,
                setValuesField: params.setValuesField,
                formMemo: params.formMemo,
                labelTransition: params.labelTransition,
                resetFunction: params.resetFunction,
                ...params.fieldId[field],
                //labelUse: params.labelUse,
              };
              //Debug(field, fieldProp);
              return (
                <DataInput
                  key={`${params.name}_${index}`}
                  {...fieldProp}
                  value={typeof fieldProp.fieldId == "string" ? params.values[fieldProp.fieldId] : params.values[field]}
                  id={field}
                  name={field}
                  onChange={(e: any) => {
                    if (fieldProp.onChange) {
                      fieldProp.onChange(e);
                    }
                    dataChanged(t, data, setData, params, e, lineTimeout);
                  }}
                />
              );
            } else {
              ("");
            }
          })}
        </>
      );
    } else {
      return <DataInput {...params} inputType="switch" defaultValue={false} itemClassName="hidden" noItem={false} />;
    }
  };
  const timelineProp: TimeLineProps & { t: TransitionType } = {
    baseTime: typeof params.indexKey == "string" ? params.indexKey : params.indexKey ? params.indexKey.join("|") : undefined,
    isMobile: isMobile,
    t: t,
    checkPoint: data?.checkPoint,
    checkValuePoint: data?.checkValue,
    comparePoint: data?.comparePoint,
    ...params.timelineProp,
    min: data?.min,
    max: data?.max,
    isAnimation: isAnimation,
  };
  const defaultClassName = chartShow ? "flex-col h-[16rem] md:h-[30rem] w-full" : " w-full";
  const xMax = (params.timelineProp?.timerLimit ? data?.valueMax.x : data?.max.x) ?? params.timelineProp?.max?.x;
  const xLimit: boolean = params.timelineProp?.timerLimit
    ? typeof params.timelineProp?.timerLimit === "string"
      ? params.inputMode === params.timelineProp?.timerLimit
      : params.timelineProp?.timerLimit instanceof Array
        ? params.timelineProp?.timerLimit.includes(params.inputMode + "")
        : params.timelineProp?.timerLimit
    : false;
  const inputEdit = params.inputMode === "write" || params.inputMode === "edit";
  const GetButtons = () => {
    return (
      <Flex
        className={cn(
          " w-full text-right relative right-2 z-10 chart-tool screenAnimation h-0",
          chartShow ? "flex-row-reverse top-[-24px]" : "h-0 md:top-4",
        )}
      >
        {(params.timelineProp?.closeButton ?? true) ? (
          <Button
            className="ml-2"
            icon={<Icon iconType="" iconName={chartShow ? "CaretLeft" : "ChartLine"} weight="bold" />}
            shape="circle"
            type={chartShow ? undefined : "primary"}
            onClick={() => {
              if (chartShow) {
                setFixedStore(fixed);
                setFixed(false);
                setTimeout(() => {
                  setChartShow((prev) => {
                    return !prev;
                  });
                }, dc.setTimeoutDelay);
              } else {
                setChartShow((prev) => {
                  return !prev;
                });
                if (fixedStore) {
                  setTimeout(() => {
                    setFixed(fixedStore);
                  }, dc.setTimeoutDelay);
                }
              }
            }}
          ></Button>
        ) : (
          ""
        )}
        {params.imageDownload ? (
          <Button
            className={cn("ml-2", chartShow ? "" : "hidden")}
            icon={<Icon iconType="" iconName="DownloadSimple" weight="bold" />}
            shape="circle"
            onClick={() => {
              exportChartAsPNG(ta, alertContext, layoutRef.current, "TimelineChart", params.imageDownload);
            }}
          ></Button>
        ) : undefined}

        {/* <Switch
          className={cn("mr-2 mt-0 ml-5 top-[-0.25rem]", chartShow ? "" : "hidden")}
          value={fixed}
          // defaultChecked={!fixed}
          checkedChildren={t("chart_fix_label")}
          unCheckedChildren={t("chart_fix_label")}
          onChange={(e) => {
            setFixed((prev) => !prev);
          }}
        /> */}
        <Button
          className={cn("ml-2 screenAnimation", chartShow ? "" : "hidden")}
          shape="circle"
          type={fixed ? "primary" : undefined}
          icon={<Icon iconType="" iconName={fixed ? "PushPinSlash" : "PushPin"} weight="bold" />}
          onClick={() => {
            setFixed((prev) => !prev);
          }}
        ></Button>
        <Button
          className={cn("ml-2", dc.compareItemClassName, chartShow ? "" : "hidden")}
          icon={<Icon iconType="" iconName="SubtractSquare" weight="bold" />}
          shape="circle"
          type={compare ? "primary" : undefined}
          onClick={() => {
            setCompare((prev) => !prev);
          }}
        ></Button>

        {/* <Button
          className="ml-2"
          icon={<Icon iconType="" iconName="Plus" weight="bold" />}
          onClick={() => {
            // setScale((s) => Math.min(s * (1 + 1 / 3), maxScale));
          }}
        ></Button> */}
        {chartShow ? <span className="mr-auto"></span> : undefined}
        {/* <Switch
          className={cn("mr-0 mt-0 top-[-0.25rem]", dc.compareItemClassName, chartShow ? "" : "hidden")}
          value={compare}
          checkedChildren={t("compare_label")}
          unCheckedChildren={t("compare_label")}
          onChange={(e) => {
            setCompare((prev) => !prev);
          }}
        /> */}
        {params.timelineProp?.timerStart || params.timelineProp?.timerStop ? (
          <>
            <Button
              className="ml-2"
              icon={<Icon iconType="" iconName={startTime ? "Stop" : "Play"} weight="bold" />}
              shape="circle"
              type={startTime ? "primary" : undefined}
              // color={startTime ? "primary" : undefined}
              // variant={startTime ? "filled" : undefined}
              onClick={() => {
                if (startTime) {
                  if (params.timelineProp?.timerStop) {
                    params.timelineProp.timerStop();
                  } else {
                    setStartTime(undefined);
                  }
                } else {
                  if (params.timelineProp?.timerStart instanceof Function) {
                    params.timelineProp.timerStart();
                  } else if (params.setTimer) {
                    const now = dayjs();
                    if (params.form) {
                      params.form.setFieldValue(params.indexKey, now);
                    }
                    runFormFunction(params.formFunction, params.indexKey, "setNow", now);
                    params.setTimer({
                      timer: params.indexKey,
                      start: dayjs(),
                      form: { form: params.form, chart: params.name, formFunction: params.formFunction },
                    });
                  }
                }
              }}
            ></Button>
            {chartShow && startTime && inputEdit ? (
              <>
                <span className="ml-3 w-0"></span>
                {params.timelineProp.timerCheck ? (
                  <Button
                    className={cn("ml-5", startTime && chartShow ? "" : "hidden")}
                    icon={<Icon iconType="" iconName="NotePencil" weight="bold" />}
                    shape="circle"
                    type={startTime ? "primary" : undefined}
                    // color={startTime ? "primary" : undefined}
                    // variant={startTime ? "filled" : undefined}
                    onClick={() => {
                      if (params.timelineProp?.timerCheck) {
                        params.timelineProp.timerCheck.onClick();
                      }
                    }}
                  ></Button>
                ) : undefined}
                {params.timelineProp.timerFind ? (
                  <Button
                    className={cn("ml-5", startTime && chartShow ? "" : "hidden")}
                    icon={<Icon iconType="" iconName="ListMagnifyingGlass" weight="bold" />}
                    shape="circle"
                    type={startTime ? "primary" : undefined}
                    // color={startTime ? "primary" : undefined}
                    // variant={startTime ? "filled" : undefined}
                    onClick={() => {
                      if (params.timelineProp?.timerFind) {
                        params.timelineProp.timerFind.onClick();
                      }
                    }}
                  ></Button>
                ) : undefined}
                {params.timelineProp.timerClock ? (
                  <Button
                    className={cn("ml-2", startTime && chartShow ? "" : "hidden")}
                    icon={<Icon iconType="" iconName="ClockUser" weight="bold" />}
                    shape="circle"
                    type={startTime ? "primary" : undefined}
                    // color={startTime ? "primary" : undefined}
                    // variant={startTime ? "filled" : undefined}
                    onClick={() => {
                      if (params.timelineProp?.timerClock) {
                        params.timelineProp.timerClock.onClick();
                      }
                    }}
                  ></Button>
                ) : undefined}
              </>
            ) : undefined}
          </>
        ) : undefined}
      </Flex>
    );
  };
  if (params.asChild ?? false) {
    return (
      <>
        {(params.timelineProp?.frontInput ?? false) ? <GetTimelineInputs {...params} asChild /> : ""}
        {(params.labelUse ?? true) && (DataLabel.display ?? true) ? <GetDataLabel {...params} /> : ""}
        <Flex className={cn(params.timelineProp?.className ?? defaultClassName, chartShow ? "" : "h-0")}>
          <Flex
            ref={layoutRef}
            className={cn(
              "data-chart TimelineChart",
              params.timelineProp?.className ?? defaultClassName,
              isMobile ? "isMobile" : "notMobile",
              chartShow ? "pt-[28px]" : "",
            )}
          >
            {GetButtons()}
            <DataTimelineItem
              {...timelineProp}
              locale={params.locale}
              chartShow={chartShow}
              compare={compare}
              xLimit={xLimit}
              xMax={xMax}
              data={data?.data}
              tempDataTitle={data?.tempDataTitle ?? {}}
              valueMax={data?.valueMax}
              seriesScale={data?.seriesScale}
              startTerm={startTerm}
              setStartTime={setStartTime}
            />
          </Flex>
        </Flex>
        {!(params.timelineProp?.frontInput ?? false) ? <GetTimelineInputs {...params} asChild /> : ""}
      </>
    );
  } else {
    const inputChild = params.timelineProp?.commonInputProp?.asChild ?? false;
    const useLabel = (params.labelUse ?? true) && (DataLabel.display ?? true);
    const mergeValueStyle = useLabel && !params.loading ? params.valueStyle : { ...params.valueStyle, gridColumn: "1 / span 2" }; //
    return (
      <>
        {(params.timelineProp?.frontInput ?? false) && !inputChild ? <GetTimelineInputs {...params} asChild={false} /> : ""}
        <DataList.Item
          className={cn(params.itemClassName, params.nextItemHidden ? "next-item-hidden" : "", (params.show ?? true) ? "" : "hidden")}
        >
          <DataList.Label className={cn(params.labelClassName, useLabel ? "" : "hidden")} style={params.labelStyle}>
            <Skeleton loading={params.loading}>{useLabel ? <GetDataLabel {...params} /> : "&nbsp;"}</Skeleton>
          </DataList.Label>
          <DataList.Value className={cn(params.valueClassName, chartShow ? "" : "h-0")} style={mergeValueStyle}>
            {(params.timelineProp?.frontInput ?? false) && inputChild ? <GetTimelineInputs {...params} asChild={true} /> : ""}
            <AntSkeleton loading={params.loading}>
              {prev}
              <Flex className={cn(params.timelineProp?.className ?? defaultClassName, chartShow ? "" : "")}>
                <Flex
                  ref={layoutRef}
                  className={cn(
                    "data-chart TimelineChart",
                    params.timelineProp?.className ?? defaultClassName,
                    isMobile ? "isMobile" : "notMobile",
                    chartShow ? "pt-[28px]" : "",
                  )}
                >
                  {GetButtons()}
                  <DataTimelineItem
                    {...timelineProp}
                    locale={params.locale}
                    chartShow={chartShow}
                    compare={compare}
                    xLimit={xLimit}
                    xMax={xMax}
                    data={data?.data}
                    tempDataTitle={data?.tempDataTitle ?? {}}
                    valueMax={data?.valueMax}
                    seriesScale={data?.seriesScale}
                    startTerm={startTerm}
                    setStartTime={setStartTime}
                  />
                </Flex>
              </Flex>
              {next}
            </AntSkeleton>
            {!(params.timelineProp?.frontInput ?? false) && inputChild ? <GetTimelineInputs {...params} asChild={true} /> : ""}
          </DataList.Value>
        </DataList.Item>
        {!(params.timelineProp?.frontInput ?? false) && !inputChild ? <GetTimelineInputs {...params} asChild={false} /> : ""}
      </>
    );
  }
}
